import { useEffect, useId, useRef, useState } from 'react';
import { Box, Button, Typography, type SxProps, type Theme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import {
  ANSWER_LENGTHS,
  ANSWER_WORDINGS,
  exampleAnswerKey,
  lengthHintKey,
  lengthLabelKey,
  wordingHintKey,
  wordingLabelKey,
  type AnswerStyle,
} from '@/components/chat/AnswerStyle';
import useAnalytics from '@/hooks/useAnalytics';
import { semanticColors } from '@/theme/tokens';

import { SECTION_LABEL_SX, optionCardSx } from './answerStyle.styles';

interface AnswerStyleEditorProps {
  value: AnswerStyle;
  onChange: (next: AnswerStyle) => void;
  /** The example starts collapsed, to keep the first read short. */
  initiallyShowExample?: boolean;
}

const QUESTION_SX: SxProps<Theme> = {
  p: 0,
  mb: '8px',
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.35,
  color: semanticColors.textPrimary,
};

/** The selected card's mark: a small brand disc with a check. */
function CheckBadge() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: 10,
        insetInlineEnd: 10,
        width: 16,
        height: 16,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: semanticColors.textBrand,
        color: semanticColors.textOnPrimary,
      }}
    >
      <CheckIcon style={{ width: 11, height: 11, strokeWidth: 3 }} />
    </Box>
  );
}

/** An unselected option's mark: an empty ring, so the card reads as a choice. */
function EmptyRing() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: 10,
        insetInlineEnd: 10,
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: `1.5px solid ${semanticColors.inputBorderHover}`,
      }}
    />
  );
}

interface OptionCardProps {
  name: string;
  value: string;
  checked: boolean;
  label: string;
  hint: string;
  hintSize: number;
  onSelect: () => void;
}

/** One option: a native radio (keyboard and screen-reader semantics) inside a card. */
function OptionCard({ name, value, checked, label, hint, hintSize, onSelect }: OptionCardProps) {
  return (
    <Box component="label" sx={optionCardSx(checked)}>
      <Box
        component="input"
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onSelect}
        sx={visuallyHidden}
      />
      {checked ? <CheckBadge /> : <EmptyRing />}
      <Typography
        component="span"
        sx={{
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.35,
          color: checked ? semanticColors.textBrand : semanticColors.textPrimary,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{ fontSize: hintSize, lineHeight: 1.4, color: semanticColors.textTertiary }}
      >
        {hint}
      </Typography>
    </Box>
  );
}

/**
 * The two answer-style questions and the local example, shared by the
 * onboarding dialog and Settings. Mirrors the mobile `AnswerStyleEditor`.
 *
 * Two separate choices, each a native radio group in a fieldset — arrow keys
 * move within a group and a screen reader names the question — drawn as equal
 * cards so no option reads as the better one: same size, same weight, no
 * badge but the selection check, no meter. The example is six fixed,
 * editorially written texts (never a model call), so changing a choice changes
 * the sample instantly and nothing else.
 */
export default function AnswerStyleEditor({
  value,
  onChange,
  initiallyShowExample = false,
}: AnswerStyleEditorProps) {
  const { t } = useTranslation('common');
  const { track } = useAnalytics();
  const [showExample, setShowExample] = useState(initiallyShowExample);
  const [announcement, setAnnouncement] = useState('');
  const baseId = useId();
  const exampleId = `${baseId}-example`;
  const summary = t('answerStyle.summary', {
    wording: t(wordingLabelKey(value.wording)),
    length: t(lengthLabelKey(value.length)),
  });

  // Announce the refreshed example politely, only after the voter changes a
  // choice while it is open — not on first render.
  const lastAnnounced = useRef(summary);
  useEffect(() => {
    if (!showExample || lastAnnounced.current === summary) return;
    lastAnnounced.current = summary;
    setAnnouncement(t('answerStyle.example.updated', { summary }));
  }, [showExample, summary, t]);

  const toggleExample = () => {
    const next = !showExample;
    setShowExample(next);
    lastAnnounced.current = summary;
    track('Answer Style Example Toggled', { expanded: next });
  };

  const paragraphs = t(exampleAnswerKey(value)).split('\n\n');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
        <Typography component="legend" sx={QUESTION_SX}>
          {t('answerStyle.wording.question')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '8px',
          }}
        >
          {ANSWER_WORDINGS.map((wording) => (
            <OptionCard
              key={wording}
              name={`${baseId}-wording`}
              value={wording}
              checked={value.wording === wording}
              label={t(wordingLabelKey(wording))}
              hint={t(wordingHintKey(wording))}
              hintSize={12}
              onSelect={() => onChange({ ...value, wording })}
            />
          ))}
        </Box>
      </Box>

      <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
        <Typography component="legend" sx={QUESTION_SX}>
          {t('answerStyle.length.question')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            // Three across where there is room; stacked rows on a phone, so a
            // long translation wraps inside its row instead of crushing a column.
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: '8px',
          }}
        >
          {ANSWER_LENGTHS.map((length) => (
            <OptionCard
              key={length}
              name={`${baseId}-length`}
              value={length}
              checked={value.length === length}
              label={t(lengthLabelKey(length))}
              hint={t(lengthHintKey(length))}
              hintSize={11}
              onSelect={() => onChange({ ...value, length })}
            />
          ))}
        </Box>
      </Box>

      <Box>
        <Button
          onClick={toggleExample}
          aria-expanded={showExample}
          aria-controls={exampleId}
          endIcon={
            <ChevronDownIcon
              aria-hidden="true"
              style={{
                width: 12,
                height: 12,
                strokeWidth: 2.5,
                transform: showExample ? 'rotate(180deg)' : 'none',
              }}
            />
          }
          sx={{
            // Shorthand: the global MuiButton padding would otherwise win.
            padding: '0 8px',
            ml: '-8px',
            minHeight: 32,
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 12,
            color: semanticColors.textBrand,
            '& .MuiButton-endIcon': { ml: '4px' },
            '&:focus-visible': {
              outline: `2px solid ${semanticColors.brandPrimary}`,
              outlineOffset: '2px',
            },
          }}
        >
          {showExample ? t('answerStyle.example.hide') : t('answerStyle.example.show')}
        </Button>

        <Box
          id={exampleId}
          role="region"
          aria-label={t('answerStyle.example.badge')}
          hidden={!showExample}
          sx={{
            mt: '8px',
            p: '12px',
            borderRadius: '12px',
            backgroundColor: semanticColors.surfaceWarm,
            display: showExample ? 'flex' : 'none',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <Typography component="span" sx={SECTION_LABEL_SX}>
            {t('answerStyle.example.badge')}
          </Typography>
          <Typography
            sx={{
              alignSelf: 'flex-end',
              maxWidth: '85%',
              px: '10px',
              py: '6px',
              borderRadius: '12px',
              backgroundColor: semanticColors.surface,
              fontSize: 12,
              lineHeight: 1.4,
              color: semanticColors.textPrimary,
            }}
          >
            {t('answerStyle.example.question')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {paragraphs.map((paragraph, index) => (
              <Typography
                key={index}
                sx={{ fontSize: 13, lineHeight: 1.55, color: semanticColors.textBody }}
              >
                {paragraph}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>

      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        {announcement}
      </Box>
    </Box>
  );
}
